<template>
    <v-dialog v-model="dialogSwitch" persistent scrollable width="700">
        <v-card>
            <v-card-title id="heading" class="d-flex justify-space-between">
                <div>
                    <h4>{{ deviceGroup?.label }}'s Device</h4>
                    <v-btn
                        class="me-3"
                        color="black"
                        size="small"
                        @click="filtersDialogSwitch = true"
                    >
                        Add Device
                    </v-btn>
                </div>
                <v-btn
                    density="compact"
                    icon="mdi-close"
                    variant="flat"
                    @click="$emit('update:modelValue', false) ;"
                />
            </v-card-title>

            <v-container>
                <v-row>
                    <v-col cols="12">
                        <v-table>
                            <thead>
                            <tr>
                                <th>No.</th>
                                <th>Device</th>
                                <th>Actions</th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr
                                v-for="(device, index) in linkDevices"
                                v-if="!loading && linkDevices.length"
                                :key="index"
                                class="text-left"
                            >
                                <td>{{ ++index }}</td>
                                <td class="text-capitalize">{{ device.slno }}</td>
                                <td>
                                    <v-btn-group density="compact" variant="outlined">
                                        <v-btn
                                            class="text-error"
                                            prepend-icon="mdi-delete"
                                            size="x-small"
                                            @click="deleteDevice(device)"
                                        >Remove
                                        </v-btn>
                                    </v-btn-group>
                                </td>
                            </tr>
                            <tr v-else>
                                <td v-if="loading" colspan="7">
                                    <v-progress-circular color="primary" indeterminate/>
                                </td>
                                <td v-else colspan="7">No record found</td>
                            </tr>
                            </tbody>
                        </v-table>
                    </v-col>
                </v-row>

                <v-row v-if="totalPages > 1" justify="center">
                    <v-col cols="8">
                        <v-pagination
                            v-model="currentPage"
                            :length="totalPages"
                            class="my-4"
                            density="compact"
                            @update:model-value="fetchDevices"
                        ></v-pagination>
                    </v-col>
                </v-row>
            </v-container>

            <v-card-actions class="px-4 d-flex justify-end">
                <v-btn
                    size="small"
                    type="button"
                    @click="$emit('update:modelValue', false);"
                >Close
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>

    <LinkDeviceGroup
        v-if="addDeviceDialogSwitch"
        v-model="addDeviceDialogSwitch"
        :devices="devices"
        :filters="filters"
        :group="deviceGroup"
        :selectedDevices="getFields(linkDevices, 'id')"
        @updateDevice="updateGroupDevices"
    />

    <filters-dialog
        v-if="filtersDialogSwitch"
        v-model="filtersDialogSwitch"
        :filters="filters"

        @applyFilters="fetchDevices"
        @resetFilters="clearFilters"
    />
</template>

<script setup>
import {send} from "@/composables/apiClient";
import {ref, reactive, onMounted, computed} from "vue";
import {useSnackbarStore} from "@/stores/snackBarStore";
import FiltersDialog from "@/components/cards/devices/FiltersDialog.vue";
import LinkDeviceGroup from "@/components/dialogs/devices/group/LinkDeviceGroup.vue";

const {showSnackbar} = useSnackbarStore();
const props = defineProps({
    modelValue: Boolean,
    deviceGroup: Object,
});

const emits = defineEmits(["update:modelValue", "newDevices"]);
const deviceStatuses = ref([
    {id: 0, title: 'Active'},
    {id: 1, title: 'Disabled'},
    {id: 2, title: 'Faulty'},
    {id: 3, title: 'Lost'}
]);

const companyLocations = ref([]);
const addDeviceDialogSwitch = ref(false);
const dialogSwitch = ref(props.modelValue);
const loading = ref(true);
const totalPages = ref(1);
const currentPage = ref(1);
const linkDevices = ref([]);
const devices = ref([]);

const filtersDialogSwitch = ref(false);
const filters = reactive({
    device_type: {label: 'Device Type', data: null, action: null},
    serial_num: {label: 'Serial Number', data: null, action: null},
    device_status: {
        label: 'Device Status',
        type: 'select',
        data: null,
        title: 'title',
        value: 'id',
        source: deviceStatuses
    },
    company_location: {
        label: 'Company Locations',
        type: 'select',
        data: null,
        title: 'title',
        value: 'id',
        multiple: true,
        source: companyLocations
    },
    geo_radius: {label: 'Radius', type: 'number', data: null, latitude: null, longitude: null},
    imei: {label: 'IMEI', data: null, action: null},
    iccid: {label: 'ICC ID', data: null, action: null},
    mr_id: {label: 'MR ID', data: null, action: null},
    bt_mac: {label: 'BT MAC', type: 'text', data: null},
    wifi_mac: {label: 'WIFI MAC', type: 'text', data: null},
    lora_mac: {label: 'LORA MAC', type: 'text', data: null},
    last_update: {label: 'Last Updated', data: null, action: null, type: 'datetime-local'},
    voltage1: {label: 'Battery Voltage', type: 'number', data: null, action: null},
});
const activeFilters = computed(() => {
    const activatedFilters = {};
    for (const [key, filter] of Object.entries(filters)) {
        if (filter) {
            if (filter.data !== null) {
                activatedFilters[key] = filter;
            }
        }
    }
    return activatedFilters;
});

const getFields = (data, field) => {
    let output = [];
    for (let i = 0; i < data.length; ++i) {
        output.push(data[i][field]);
    }
    return output ?? [];
}

onMounted(() => {
    fetchDevices();
});

const fetchDevices = () => {
    loading.value = true;
    let filtered = Object.values(activeFilters.value).length;
    send(`${filtered ? "post" : "get"}`,
        `${filtered ? `/device-groups/${props.deviceGroup.id}/filtered` : `device-groups/${props.deviceGroup.id}/devices?page=${currentPage.value}`}`,
        filtered ? filters : null
    )
        .then((response) => {
            if (filtered) {
                showDevices(response.data)
            } else {
                linkDevices.value = response.data.data.devices.data;
                totalPages.value = response.data.data.devices.last_page;
                currentPage.value = response.data.data.devices.current_page;
            }
        })
        .catch((error) => {
            showSnackbar("Devices could not be loaded", "error");
        })
        .finally(() => loading.value = false);
};

const updateGroupDevices = (response) => {
    emits('newDevices');
    linkDevices.value = response.data.data.devices.data;
    totalPages.value = response.data.data.devices.last_page;
    currentPage.value = response.data.data.devices.current_page;
}

const showDevices = (response) => {
    devices.value = response.data.devices;
    addDeviceDialogSwitch.value = true;
}

const clearFilters = () => {
    Object.keys(filters).forEach((key) => {
        filters[key]['data'] = null;
        filters[key]['action'] ? filters[key]['action'] = null : ''
    });
    fetchDevices();
}

const deleteDevice = (device) => {
    if (
        confirm(`Are you sure to remove "${device.slno}" from ${props.deviceGroup.label}?`)
    ) {
        send("delete", `device-groups/${props.deviceGroup.id}/devices/${device.id}`)
            .then((response) => {
                if (response.data.success) {
                    linkDevices.value = response.data.data.devices.data;
                    totalPages.value = response.data.data.devices.last_page;
                    currentPage.value = response.data.data.devices.current_page;
                    loading.value = false;
                    showSnackbar(response.data.message);
                    emits('newDevices');
                }
            })
            .catch((error) => {
                console.log(error);
                showSnackbar(`"${device.slno}" device could not be deleted`, "error");
            });
    }
};
</script>
